import React, { useEffect, useState, useCallback } from 'react';
import { shippingPriceByCapacityApi, shippingPriceApi } from '../services/api';
import { ShippingPriceMatrix, Province, BulkPriceUpdateItem } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { HistoryOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

interface EditedCell {
  key: string;
  provinceId?: number;
  districtId?: number;
capacityTonsId: number;
  price: number;
  notes?: string;
  affectedDate?: string;
}

interface ShippingPriceMatrixPageProps {
  embedded?: boolean; // When true, removes container wrapper for embedding in other pages
}

const ShippingPriceMatrixPage: React.FC<ShippingPriceMatrixPageProps> = ({ embedded = false }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [matrix, setMatrix] = useState<ShippingPriceMatrix | null>(null);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedProvince, setSelectedProvince] = useState<number | undefined>(undefined);
  const [locationType, setLocationType] = useState<string>('District');
  const [editedCells, setEditedCells] = useState<Map<string, EditedCell>>(new Map());
  const [reason, setReason] = useState('');

  const formatVietnamCurrency = (amount: number): string => {
    return new Intl.NumberFormat('vi-VN').format(amount) + ' ₫';
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [matrixResponse, provincesResponse] = await Promise.all([
        shippingPriceByCapacityApi.getMatrix(selectedProvince, locationType),
        shippingPriceApi.getProvinces()
      ]);
      setMatrix(matrixResponse.data);
      setProvinces(provincesResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProvince, locationType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getCellKey = (row: any, capacityId: number): string => {
    return `${row.provinceId || ''}-${row.districtId || ''}-${capacityId}`;
  };

  const handleCellChange = (row: any, capacityId: number, value: string) => {
  const key = getCellKey(row, capacityId);
    const numValue = parseFloat(value.replace(/[^0-9.]/g, ''));
    
    if (isNaN(numValue)) {
      const newEdited = new Map(editedCells);
      newEdited.delete(key);
      setEditedCells(newEdited);
    return;
    }

    const newEdited = new Map(editedCells);
    newEdited.set(key, {
      key,
      provinceId: row.provinceId,
      districtId: row.districtId,
      capacityTonsId: capacityId,
      price: numValue,
   affectedDate: new Date().toISOString()
    });
    setEditedCells(newEdited);
  };

  const getCellValue = (row: any, capacityId: number): string => {
 const key = getCellKey(row, capacityId);
    const edited = editedCells.get(key);
    if (edited) {
      return edited.price.toString();
    }
    const priceInfo = row.capacityPrices?.[capacityId];
    return priceInfo?.price?.toString() || '';
  };

  const isCellEdited = (row: any, capacityId: number): boolean => {
    const key = getCellKey(row, capacityId);
    return editedCells.has(key);
  };

  const isCellOverride = (row: any, capacityId: number): boolean => {
    const priceInfo = row.capacityPrices?.[capacityId];
    return priceInfo?.isOverride === true;
  };

  const handleSaveChanges = async () => {
    if (editedCells.size === 0) {
 alert(t('no.changes.to.save') || 'No changes to save');
      return;
    }

    setSaving(true);
    try {
  const updates: BulkPriceUpdateItem[] = Array.from(editedCells.values()).map(cell => ({
   provinceId: cell.provinceId,
        districtId: cell.districtId,
   capacityTonsId: cell.capacityTonsId,
        price: cell.price,
        notes: cell.notes,
affectedDate: cell.affectedDate
      }));

      await shippingPriceByCapacityApi.bulkUpdate({
        updates,
        reason: reason || undefined
      });

 setEditedCells(new Map());
    setReason('');
      await fetchData();
      alert(t('changes.saved.successfully') || 'Changes saved successfully!');
    } catch (error: any) {
  console.error('Error saving changes:', error);
      alert(error?.response?.data?.message || t('error.saving.changes') || 'Error saving changes');
    } finally {
      setSaving(false);
 }
  };

  const handleDiscardChanges = () => {
    if (editedCells.size > 0 && !window.confirm(t('confirm.discard.changes') || 'Discard all unsaved changes?')) {
      return;
    }
    setEditedCells(new Map());
    setReason('');
  };

  const content = (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
  <h5 className="mb-0">{t('shipping.price.matrix') || 'Shipping Price by Capacity'}</h5>
     <div className="d-flex gap-2">
     <button 
            className="btn btn-outline-secondary btn-sm" 
 onClick={() => navigate('/pricing/capacity-history')}
     title={t('view.history') || 'View History'}
  >
            <HistoryOutlined /> {t('history') || 'History'}
      </button>
     </div>
      </div>

      {/* Info Banner */}
      <div className="alert alert-info mb-3 py-2">
        <small>
     <strong>{t('how.it.works') || 'How it works'}:</strong>{' '}
       {t('base.price.explanation') || 'Base prices (shown in light blue) apply to all vehicle capacities. Edit a cell to create a capacity-specific override (shown in white). Overrides take priority when calculating shipping costs.'}
        </small>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2">
            <div className="col-md-3">
      <label className="form-label small mb-1">{t('province') || 'Province'}</label>
              <select 
      className="form-select form-select-sm"
         value={selectedProvince || ''}
          onChange={(e) => setSelectedProvince(e.target.value ? parseInt(e.target.value) : undefined)}
 >
   <option value="">{t('all.provinces') || 'All Provinces'}</option>
      {provinces.map(p => (
        <option key={p.id} value={p.id}>{p.name}</option>
       ))}
    </select>
          </div>
            <div className="col-md-3">
    <label className="form-label small mb-1">{t('location.type') || 'Location Type'}</label>
  <select 
                className="form-select form-select-sm"
     value={locationType}
           onChange={(e) => setLocationType(e.target.value)}
 >
        <option value="">{t('all') || 'All'}</option>
<option value="Province">{t('province') || 'Province'}</option>
           <option value="District">{t('district') || 'District'}</option>
   </select>
      </div>
            <div className="col-md-4">
           <label className="form-label small mb-1">{t('reason.for.changes') || 'Reason for Changes'}</label>
           <input
       type="text"
             className="form-control form-control-sm"
                value={reason}
  onChange={(e) => setReason(e.target.value)}
              placeholder={t('enter.reason') || 'Enter reason for price changes...'}
          />
         </div>
            <div className="col-md-2 d-flex align-items-end gap-2">
  <button 
   className="btn btn-primary btn-sm flex-grow-1" 
                onClick={handleSaveChanges}
         disabled={saving || editedCells.size === 0}
     >
 {saving ? (
    <span className="spinner-border spinner-border-sm me-1" />
        ) : (
          <SaveOutlined className="me-1" />
                )}
                {t('save') || 'Save'} ({editedCells.size})
            </button>
   {editedCells.size > 0 && (
            <button 
             className="btn btn-outline-secondary btn-sm" 
     onClick={handleDiscardChanges}
title={t('discard.changes') || 'Discard Changes'}
>
        <ReloadOutlined />
        </button>
       )}
        </div>
          </div>
 </div>
      </div>

      {/* Matrix Table */}
      {loading ? (
        <div className="text-center my-5">
 <div className="spinner-border" role="status">
   <span className="visually-hidden">{t('loading') || 'Loading...'}</span>
          </div>
  </div>
  ) : matrix && matrix.rows.length > 0 ? (
        <div className="card">
          <div className="card-body p-0">
            <div className="table-responsive" style={{ maxHeight: '60vh' }}>
        <table className="table table-bordered table-hover table-sm mb-0">
    <thead className="table-light sticky-top">
    <tr>
    <th style={{ minWidth: '50px', position: 'sticky', left: 0, background: '#f8f9fa', zIndex: 3 }}>
     #
       </th>
   <th style={{ minWidth: '200px', position: 'sticky', left: '50px', background: '#f8f9fa', zIndex: 2 }}>
           {t('location') || 'Location'}
     </th>
   <th style={{ minWidth: '100px' }}>{t('base.price') || 'Base Price'}</th>
  {matrix.capacityColumns.map(cap => (
       <th key={cap.id} className="text-center" style={{ minWidth: '120px' }}>
           {cap.capacity} {t('tons') || 'T'}
     {cap.description && <small className="d-block text-muted">{cap.description}</small>}
           </th>
     ))}
            </tr>
</thead>
          <tbody>
          {matrix.rows.map((row, index) => (
        <tr key={`${row.provinceId}-${row.districtId}`}>
          <td className="text-center" style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
         {index + 1}
       </td>
  <td style={{ position: 'sticky', left: '50px', background: '#fff', zIndex: 1 }}>
    <strong>{row.locationName}</strong>
         {row.provinceName && (
     <small className="d-block text-muted">{row.provinceName}</small>
          )}
         <span className={`badge ${row.locationType === 'Province' ? 'bg-primary' : 'bg-secondary'} ms-1`}>
        {t(row.locationType.toLowerCase()) || row.locationType}
    </span>
         </td>
   <td className="text-end table-info">
           <strong>{row.basePrice ? formatVietnamCurrency(row.basePrice) : '-'}</strong>
           </td>
            {matrix.capacityColumns.map(cap => {
           const isEdited = isCellEdited(row, cap.id);
     const isOverride = isCellOverride(row, cap.id);
            const cellValue = getCellValue(row, cap.id);
           
      let cellBg = '#e7f3ff';
       if (isEdited) {
cellBg = '#fff3cd';
} else if (isOverride) {
   cellBg = '#fff';
       }

return (
   <td 
  key={cap.id} 
     className="text-end"
 style={{ padding: '0.25rem' }}
  >
 <input
    type="text"
         className="form-control form-control-sm text-end"
  value={cellValue}
            onChange={(e) => handleCellChange(row, cap.id, e.target.value)}
    placeholder={row.basePrice?.toString() || '-'}
         style={{ 
             background: cellBg,
      border: isEdited ? '2px solid #ffc107' : (isOverride ? '1px solid #198754' : '1px solid #dee2e6')
        }}
 title={isOverride ? (t('custom.override') || 'Custom override') : (t('using.base.price') || 'Using base price')}
   />
   </td>
       );
     })}
        </tr>
        ))}
       </tbody>
     </table>
        </div>
     </div>
        </div>
      ) : (
        <div className="alert alert-warning">
          {t('no.base.prices.found') || 'No base prices found. Please add locations in the Shipping Prices tab first.'}
   </div>
   )}

      {/* Legend */}
 <div className="mt-3">
<small className="text-muted">
          <span className="me-3">
  <span className="badge" style={{ background: '#e7f3ff', color: '#000', border: '1px solid #dee2e6' }}>●</span>{' '}
        {t('using.base.price') || 'Using base price'}
          </span>
          <span className="me-3">
     <span className="badge" style={{ background: '#fff', color: '#000', border: '1px solid #198754' }}>●</span>{' '}
    {t('custom.override') || 'Custom override'}
          </span>
          <span className="me-3">
   <span className="badge bg-warning text-dark">●</span>{' '}
         {t('unsaved.changes') || 'Unsaved changes'}
          </span>
        </small>
      </div>
    </>
  );

  // If embedded, return just the content without container wrapper
  if (embedded) {
    return content;
  }

  return (
    <div className="container-fluid">
      {content}
    </div>
  );
};

export default ShippingPriceMatrixPage;
